import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as { from?: string } | null)?.from ?? "/";

  useEffect(() => {
    document.title = "Pegasus · Sign in";
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: signInError } = await signIn(email.trim(), password);
    setSubmitting(false);

    if (signInError) {
      setError(signInError.message || "Sign in failed. Please try again.");
      return;
    }
    navigate(from, { replace: true });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 px-4">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="flex w-full max-w-md flex-col items-center"
      >
        <div className="mb-8 flex w-full flex-col items-center gap-3">
          {/* Symbol-only mark plus the wordmark rendered as HTML text.
              This avoids the asymmetric-whitespace issue baked into the
              full logo file and gives us pixel-perfect centering. */}
          <img
            src="/pegasus-mark.png"
            alt="Pegasus"
            className="h-16 w-auto object-contain"
          />
          <h1 className="text-3xl font-light tracking-[0.2em] text-slate-800">
            PEGASUS
          </h1>
          <p className="text-sm text-slate-500">Sedgwick Heating &middot; Sign in</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="w-full space-y-4 rounded-xl border border-hairline bg-white p-6 shadow-e2"
        >
          <div>
            <label
              htmlFor="email"
              className="mb-1.5 block text-xs font-medium text-slate-700"
            >
              Email
            </label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@sedgwickheating.com"
              disabled={submitting}
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="mb-1.5 block text-xs font-medium text-slate-700"
            >
              Password
            </label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}

          <Button type="submit" loading={submitting} className="w-full">
            Sign in
          </Button>
        </form>

        <p className="mt-6 text-center text-xs text-slate-500">
          v0.1.0 &middot; Sedgwick Heating internal CRM
        </p>
      </motion.div>
    </div>
  );
}
